/** Canonical ordering: numeric-aware string sort. */
function canonicalSort(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  if (!isNaN(na) && !isNaN(nb)) {
    return na - nb;
  }
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Dedup and sort an array into canonical order. */
function canonicalize<T extends string>(xs: T[]): T[] {
  return [...new Set(xs)].sort(canonicalSort);
}

export type PairRelation = 'disjoint' | 'subset' | 'superset' | 'equal' | 'overlap';

export function union<T extends string>(a: T[], b: T[]): T[] {
  return canonicalize([...a, ...b]);
}

export function intersect<T extends string>(a: T[], b: T[]): T[] {
  const bSet = new Set<string>(b);
  return canonicalize(a.filter((x) => bSet.has(x)));
}

export function difference<T extends string>(a: T[], b: T[]): T[] {
  const bSet = new Set<string>(b);
  return canonicalize(a.filter((x) => !bSet.has(x)));
}

export function symmetricDifference<T extends string>(a: T[], b: T[]): T[] {
  return union(difference(a, b), difference(b, a));
}

export function complement<T extends string>(universe: T[], a: T[]): T[] {
  return difference(universe, a);
}

export function isSubset<T extends string>(a: T[], b: T[]): boolean {
  const bSet = new Set<string>(b);
  return a.every((x) => bSet.has(x));
}

export function setsEqual<T extends string>(a: T[], b: T[]): boolean {
  return isSubset(a, b) && isSubset(b, a);
}

export function cardinality<T extends string>(a: T[]): number {
  return new Set(a).size;
}

export function classifyPair<T extends string>(a: T[], b: T[]): PairRelation {
  if (setsEqual(a, b)) {
    return 'equal';
  }
  if (isSubset(a, b)) {
    return 'subset';
  }
  if (isSubset(b, a)) {
    return 'superset';
  }
  if (intersect(a, b).length === 0) {
    return 'disjoint';
  }
  return 'overlap';
}
